import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { tt } from '../utils/consoleText';
import {
  MAIN_NAV_DEFINITIONS,
  applySidebarConfig,
  localizeNavigationDefinitions,
  prefixNavigationPath,
} from '@/constants/navigation';
import { useDashboardStore } from '@/stores/dashboard';
import { useSidebarConfigStore } from '@/stores/sidebarConfig';
import { isLayoutDashboard } from '@/utils/layoutMarker';
import { isSystemPageRow } from '@/utils/systemPages';

export type NavigationTargetSectionId = 'system' | 'main';
export type NavigationTargetKind = 'page' | 'primary-dashboard' | 'dashboard-page';

export interface NavigationTargetSelection {
  targetId: string;
  targetKind: NavigationTargetKind;
}

export interface NavigationTarget {
  id: string;
  kind: NavigationTargetKind;
  sectionId: NavigationTargetSectionId;
  label: string;
  icon: string;
  route: string;
  pinned: boolean;
  order: number;
}

export interface NavigationTargetSection {
  id: NavigationTargetSectionId;
  title: string;
  items: NavigationTarget[];
}

interface CatalogPageItem {
  id: string;
  icon: string;
  label: string;
  route: string;
  hidden: boolean;
  order: number;
}

function withResolvedPath(path: string | undefined, hubId: string | undefined): string {
  return prefixNavigationPath(path, hubId) ?? '/';
}

export function isNavigationTargetKind(value: string | null | undefined): value is NavigationTargetKind {
  return value === 'page' || value === 'primary-dashboard' || value === 'dashboard-page';
}

export function buildNavigationTargetKey(selection: NavigationTargetSelection): string {
  return `${selection.targetKind}:${selection.targetId}`;
}

export function parseNavigationTargetKey(value: string): NavigationTargetSelection | null {
  const delimiterIndex = value.indexOf(':');
  if (delimiterIndex <= 0) {
    return null;
  }

  const targetKind = value.slice(0, delimiterIndex);
  const targetId = value.slice(delimiterIndex + 1);

  if (!isNavigationTargetKind(targetKind) || !targetId) {
    return null;
  }

  return {
    targetId,
    targetKind,
  };
}

export function useNavigationTargets() {
  const { t } = useI18n();
  const route = useRoute();
  const sidebarConfig = useSidebarConfigStore();
  const dashboardStore = useDashboardStore();

  const hubId = computed(() => route.params.hubId as string | undefined);

  /** Is there a 3D page to open? The hub has at most one (`hubPage = true`). */
  const hasModelPage = computed(() => dashboardStore.modelPage !== null);

  function buildDashboardRoute(dashboardId: string): string {
    const basePath = withResolvedPath('/layouts', hubId.value);
    return `${basePath}/${dashboardId}`;
  }

  const mainDefinitionItems = computed<CatalogPageItem[]>(() =>
    applySidebarConfig(localizeNavigationDefinitions(MAIN_NAV_DEFINITIONS, t), sidebarConfig.items).map((item) => ({
      id: item.id,
      icon: item.icon,
      label: item.label,
      route: withResolvedPath(item.path, hubId.value),
      hidden: item.hidden,
      order: item.order,
    })),
  );

  const ungroupedDashboardTargets = computed<NavigationTarget[]>(() => {
    const items = dashboardStore.dashboards
      // The dashboard and the 3D house are the app's, not rows an owner made:
      // their nav entries are static (`navigation.ts`) and their URLs are their
      // own, so offering them here would be the second door to one room.
      .filter((dashboard) => !isLayoutDashboard(dashboard) && !isSystemPageRow(dashboard))
      .map((dashboard, index) => ({
        id: dashboard.id,
        kind: 'dashboard-page' as const,
        sectionId: 'system' as const,
        label: dashboard.name,
        icon: dashboard.icon || 'dashboard',
        route: buildDashboardRoute(dashboard.id),
        pinned: !sidebarConfig.resolveHidden(dashboard.id, false),
        order: sidebarConfig.getOrder(dashboard.id, index),
      }));

    return items.sort((left, right) => left.order - right.order);
  });

  const mainPageTargets = computed<NavigationTarget[]>(() => {
    return mainDefinitionItems.value
      // The 3D house is a system page the hub may not have: its row exists only
      // once a model is uploaded, so its nav entry appears with the row rather
      // than sending anyone to a surface with nothing in it.
      .filter((item) => item.id !== 'model' || hasModelPage.value)
      .map((item) => ({
        id: item.id,
        kind: 'page' as const,
        sectionId: 'main' as const,
        label: item.label,
        icon: item.icon,
        route: item.route,
        pinned: !item.hidden,
        order: item.order,
      }));
  });

  const launcherSections = computed<NavigationTargetSection[]>(() => {
    const sections: NavigationTargetSection[] = [
      {
        id: 'system',
        title: tt('Dashboards', 'Nadzorne plošče'),
        items: [...ungroupedDashboardTargets.value],
      },
      {
        id: 'main',
        title: tt('Built-in pages', 'Vgrajene strani'),
        items: mainPageTargets.value,
      },
    ];

    return sections.filter((section) => section.items.length > 0);
  });

  const allTargets = computed<NavigationTarget[]>(() => launcherSections.value.flatMap((section) => section.items));

  const targetMap = computed(() => {
    const map = new Map<string, NavigationTarget>();

    for (const item of allTargets.value) {
      map.set(buildNavigationTargetKey({ targetId: item.id, targetKind: item.kind }), item);
    }

    return map;
  });

  function findNavigationTarget(selection: NavigationTargetSelection | null | undefined): NavigationTarget | undefined {
    if (!selection?.targetId || !selection.targetKind) {
      return undefined;
    }

    return targetMap.value.get(buildNavigationTargetKey(selection));
  }

  function resolveNavigationTargetRoute(selection: NavigationTargetSelection | null | undefined): string | undefined {
    return findNavigationTarget(selection)?.route;
  }

  return {
    allTargets,
    buildDashboardRoute,
    findNavigationTarget,
    launcherSections,
    mainPageTargets,
    resolveNavigationTargetRoute,
    ungroupedDashboardTargets,
  };
}
